import type { UnderscoredStepNames } from 'router/types';

import type { Lead } from 'providers/LeadProvider';
import type { Country } from 'services/i18n';

const GROWTHBOOK_FLAGS_PREFIX = 'partners.acquisition.';

type GROWTHBOOK_FLAGS_NAME = `${typeof GROWTHBOOK_FLAGS_PREFIX}${string}`;

export const GROWTHBOOK_DECISION_FLAGS = [
  'partners.acquisition.real-time-offer',
  'partners.acquisition.bypass-activities',
  'partners.acquisition.wellhub-usc-flow',
  'partners.acquisition.coupon-code',
  'partners.acquisition.wellness-center',
  'partners.acquisition.member-access-revamp',
  'partners.acquisition.wellhub-club',
] as const satisfies Array<GROWTHBOOK_FLAGS_NAME>;

type ConversionAction = 'completed';

// The first value listed for each flag is its default: app call sites take
// their evaluation fallback from GROWTHBOOK_ENUM_DEFAULTS below, and test
// flag defaults derive from it too, so "first = default" is enforced by use
// rather than by convention. Reordering a list changes the app's fallback.
export const GROWTHBOOK_ENUM_FLAGS = {
  'partners.acquisition.commercial-conditions': [
    'ALL_CCS',
    'ONLY_CAP',
    'CAP_AND_FVF',
    'CAP_AND_SF',
  ],
} as const satisfies Record<
  GROWTHBOOK_FLAGS_NAME,
  readonly [string, ...string[]]
>;

type GrowthbookEnumFlags = typeof GROWTHBOOK_ENUM_FLAGS;
type GrowthbookEnumDefaults = {
  [Flag in keyof GrowthbookEnumFlags]: GrowthbookEnumFlags[Flag][0];
};

export const GROWTHBOOK_ENUM_DEFAULTS = Object.fromEntries(
  Object.entries(GROWTHBOOK_ENUM_FLAGS).map(([flag, values]) => [
    flag,
    values[0],
  ]),
) as GrowthbookEnumDefaults;

type StepsEvents = `partner_ssu_${UnderscoredStepNames}_${ConversionAction}`;

const gymStepsExperimentEvents = [
  'partner_ssu_activities_completed',
  'partner_ssu_pricing_completed',
  'partner_ssu_offer_completed',
  'partner_ssu_gym_business_details_completed',
  'partner_ssu_gym_tax_details_completed',
] as const satisfies StepsEvents[];

const rtoExperimentEvents = [
  'partner_real_time_offer_accept',
  'partner_real_time_offer_decline',
] as const;

const availableEventNames = [
  'partner_self_sign_up_completed',
  'partner_start_completed',
  'partner_ssu_terms_and_conditions_completed',
  'partner_ssu_tc_offer_decline_submitted',
  'partners_ssu_bypass_activities',
  'partner_ssu_usc_redirect',
  ...rtoExperimentEvents,
  ...gymStepsExperimentEvents,
] as const;

export const DEFAULT_ACTIVITY_FALLBACK = {
  defaultValue: 'Wellness',
  id: 'c99caa3b-fa4e-11ed-9403-0a62c7fc40df',
  translationKey:
    'partner_activities:activity.c99caa3b-fa4e-11ed-9403-0a62c7fc40df',
} as const;

export type ConversionEvents = (typeof availableEventNames)[number];

/**
 * Every attribute this app sets on the GrowthBook client, across the
 * session defaults (`buildDefaultAttributes`) and every `updateAttributes`
 * call site. Written updates are always a subset of this shape
 * (`Partial<GrowthBookAttributes>`) — the client merges them onto whatever
 * is already set.
 *
 * `country` / `country_code` allow `''` on top of a real `Country`: the
 * Business Details Country field clears both back to empty on close (see
 * `useCountryChange`), and that clear is a real, intentional write, not a
 * missing value.
 */
export type GrowthBookAttributes = {
  // set once a lead exists (Starting step onward) — absent for the earlier
  // part of a session
  lead_id?: string;
  business_type?: Lead['businessType'];
  country: Country | '';
  country_code: Uppercase<Country> | '';
  lead_created_at?: string;
  // set once at GrowthBook client construction (buildDefaultAttributes)
  app_version: string;
  platform: 'web';
  timezone: string;
  user_agent: string;
  session_id: string;
  anonymous_id?: string;
  is_bot: boolean;
  app_id: string;
  // set by individual writers
  accept_language?: string;
  opportunity_id?: string;
};
